import React, { useState } from 'react';
import { WordpressArticle, cn } from '@ais-ui/utils';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@ais-ui/ui-core';
import { PiCaretDown, PiCaretRight } from 'react-icons/pi';
import { categoryToIcon } from './categoryToIcon';

interface HelpSidebarProps {
  articles: WordpressArticle[];
  activeArticleId?: number;
  onArticleSelect: (article: WordpressArticle) => void;
  searchQuery?: string;
}

interface CategoryGroup {
  name: string;
  articles: WordpressArticle[];
}

export const HelpSidebar: React.FC<HelpSidebarProps> = ({
  articles,
  activeArticleId,
  onArticleSelect,
  searchQuery = '',
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );

  // Group articles by category
  const categoryGroups: CategoryGroup[] = React.useMemo(() => {
    const groups = new Map<string, WordpressArticle[]>();

    articles.forEach((article) => {
      const category = article.categories?.[0] || 'General';
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)!.push(article);
    });

    return Array.from(groups.entries())
      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
      .map(([name, articles]) => ({
        name,
        articles: articles.sort((a, b) =>
          a.title.rendered.localeCompare(b.title.rendered)
        ),
      }));
  }, [articles]);

  const toggleCategory = (categoryName: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName);
    } else {
      newExpanded.add(categoryName);
    }
    setExpandedCategories(newExpanded);
  };

  // Auto-expand categories that contain search results
  React.useEffect(() => {
    if (searchQuery) {
      const newExpanded = new Set<string>();
      categoryGroups.forEach((group) => {
        const hasMatch = group.articles.some(
          (article) =>
            article.title.rendered
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            article.subtitle?.toLowerCase().includes(searchQuery.toLowerCase())
        );
        if (hasMatch) {
          newExpanded.add(group.name);
        }
      });
      setExpandedCategories(newExpanded);
    }
  }, [searchQuery, categoryGroups]);

  return (
    <div className="px-4 space-y-2">
      {categoryGroups.map((group) => {
        const CategoryIcon = categoryToIcon([group.name]);
        const isExpanded = expandedCategories.has(group.name);

        return (
          <Collapsible
            key={group.name}
            open={isExpanded}
            onOpenChange={() => toggleCategory(group.name)}
          >
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-start p-2 h-auto font-medium text-sm"
              >
                {isExpanded ? (
                  <PiCaretDown className="h-4 w-4 mr-2" />
                ) : (
                  <PiCaretRight className="h-4 w-4 mr-2" />
                )}
                <CategoryIcon className="h-4 w-4 mr-2" />
                <span className="capitalize">{group.name}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {group.articles.length}
                </span>
              </Button>
            </CollapsibleTrigger>

            <CollapsibleContent className="ml-6 space-y-1">
              {group.articles.map((article) => {
                const isActive = article.id === activeArticleId;
                const matchesSearch =
                  !searchQuery ||
                  article.title.rendered
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                  article.subtitle
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase());

                if (!matchesSearch) return null;

                return (
                  <Button
                    key={article.id}
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'w-full justify-start p-2 h-auto text-left font-normal',
                      isActive && 'bg-accent text-accent-foreground',
                      searchQuery &&
                        matchesSearch &&
                        'bg-yellow-50 dark:bg-yellow-900/20'
                    )}
                    onClick={() => onArticleSelect(article)}
                  >
                    <span className="text-sm leading-tight">
                      {article.title.rendered}
                    </span>
                  </Button>
                );
              })}
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
};
